// packages/app/src/scaffolder/ApplicationKeyPicker.tsx
//
// Dynamic application dropdown for scaffolder templates.
// - Plat users (eks-team-plat-lead, eks-team-plat-dev) see ALL applications
// - Non-plat users see only applications belonging to their own teams
// - Shows: "App Name (app-key)" in dropdown
// - Submits: app_key as value

import { useEffect, useState } from 'react';
import { useApi, fetchApiRef, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import CircularProgress from '@material-ui/core/CircularProgress';
import { parseEksTeam } from '../utils/eksTeam';

interface Application {
  app_key:  string;
  app_name: string;
  team_key: string;
}

function extractTeamKey(ref: string): string | null {
  const parsed = parseEksTeam(ref);
  return parsed ? parsed.teamKey : null;
}

export const ApplicationKeyPickerComponent = ({
  onChange,
  rawErrors,
  formData,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const fetchApi    = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [apps, setApps]       = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      identityApi.getBackstageIdentity(),
      // discoveryApi resolves the real backend URL and the token is attached
      // explicitly — see backendFetch in DashboardPage.tsx for why a plain
      // relative fetchApi.fetch('/api/applications') stops authenticating
      // once frontend and backend are on different origins.
      Promise.all([discoveryApi.getBaseUrl('applications'), identityApi.getCredentials()])
        .then(([baseUrl, { token }]) =>
          fetchApi.fetch(baseUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }),
        )
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        }),
    ])
      .then(([identity, data]) => {
        const refs       = identity.ownershipEntityRefs ?? [];
        const isPlatUser = refs.some(ref => {
          const parsed = parseEksTeam(ref);
          return parsed !== null && parsed.isPlatform && (parsed.role === 'lead' || parsed.role === 'dev');
        });

        const allApps: Application[] = data.applications ?? [];

        if (isPlatUser) {
          setApps(allApps);
        } else {
          const teamKeys = new Set(
            refs.map(extractTeamKey).filter((k): k is string => k !== null),
          );
          setApps(allApps.filter(a => teamKeys.has(a.team_key)));
        }
      })
      .catch(() => {
        setError('Could not load applications. Please refresh.');
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasError = (rawErrors?.length ?? 0) > 0;

  if (loading) {
    return (
      <FormControl fullWidth>
        <InputLabel shrink>{schema?.title ?? 'Application'}</InputLabel>
        <CircularProgress size={20} style={{ marginTop: 24 }} />
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth error>
        <InputLabel shrink>{schema?.title ?? 'Application'}</InputLabel>
        <FormHelperText>{error}</FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth error={hasError}>
      <InputLabel shrink>{schema?.title ?? 'Application'}</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(e.target.value as string)}
        displayEmpty
      >
        <MenuItem value="" disabled>
          <em>Select an application...</em>
        </MenuItem>
        {apps.map(app => (
          <MenuItem key={app.app_key} value={app.app_key}>
            {app.app_name} ({app.app_key})
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {hasError
          ? rawErrors![0]
          : schema?.description ?? 'Select the application this service belongs to'}
      </FormHelperText>
    </FormControl>
  );
};
